import type { ReactNode } from 'react';
import { Star } from 'lucide-react';
import { PaymentMethodIcon } from './CardBrandIcon';
import { formatDateInBusinessTZ } from '../utils/date';
import type { PaymentMethodDisplay } from '../utils/paymentMethodDisplay';

interface PaymentMethodRowProps {
  method: PaymentMethodDisplay;
  // Trailing controls after the Default pill (e.g. the parent's options menu)
  actions?: ReactNode;
}

/**
 * One saved payment method, as the parent sees it on their Account page. Render inside a
 * `rounded-xl border divide-y` container so consecutive rows share hairline dividers.
 */
export function PaymentMethodRow({ method, actions }: PaymentMethodRowProps) {
  const expiry =
    method.type === 'card' && method.expMonth && method.expYear
      ? `Expires ${String(method.expMonth).padStart(2, '0')}/${String(method.expYear).slice(-2)}`
      : null;
  const lastUsedLabel = method.lastUsed
    ? `Last used ${formatDateInBusinessTZ(method.lastUsed)}`
    : null;

  return (
    <div
      className={`relative flex items-center justify-between gap-3 px-4 py-3.5 transition-colors ${
        method.isDefault ? 'bg-blue-50/60' : 'bg-white hover:bg-gray-50/70'
      }`}
    >
      <div className="flex items-center min-w-0">
        <div className="mr-3 shrink-0">
          <PaymentMethodIcon type={method.type} brand={method.brand} size={26} />
        </div>
        <div className="min-w-0">
          <p className="text-sm sm:text-base font-medium text-gray-900 truncate">{method.name}</p>
          <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 mt-0.5 text-xs sm:text-sm text-gray-500">
            {method.type === 'bank_account' && (
              method.bankVerificationStatus === 'verified' ? (
                <span className="inline-flex items-center gap-1 text-green-700 font-medium">
                  <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
                  Verified
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 text-amber-700 font-medium">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                  Pending verification
                </span>
              )
            )}
            {method.detail && <span>{method.detail}</span>}
            {expiry && <span>{expiry}</span>}
            {method.feeNote && (
              <span className="text-amber-700 font-medium">{method.feeNote}</span>
            )}
            {lastUsedLabel && <span>{lastUsedLabel}</span>}
          </div>
        </div>
      </div>

      <div className="flex items-center gap-2 shrink-0">
        {method.isDefault && (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold text-blue-700 bg-blue-100">
            <Star className="w-3 h-3 fill-blue-600 text-blue-600" />
            Default
          </span>
        )}
        {actions}
      </div>
    </div>
  );
}
